import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
    FaArrowLeft,
    FaArrowRight,
    FaBookOpen,
    FaBullseye,
    FaChartLine,
    FaCheckCircle,
    FaChevronLeft,
    FaChevronRight,
    FaClock,
    FaCompass,
    FaGraduationCap,
    FaLightbulb,
    FaPlay,
    FaSeedling,
    FaUsers,
} from "react-icons/fa";

// ============================================================
// CAYEP LOCAL IMAGES
// Replace/add your actual local CAYEP image files here.
// ============================================================

import leadershipImage from "./TrainingProgramImages/cayep1.jpg";
import managementImage from "./TrainingProgramImages/cayep2.jpg";
import entrepreneurshipImage from "./TrainingProgramImages/entrepreneurship.jpg";
import communicationImage from "./TrainingProgramImages/cayep3.jpg";

import leadershipGallery1 from "./TrainingProgramImages/cayep5.jpg";
import leadershipGallery2 from "./TrainingProgramImages/entrepreneurship.jpg";
import leadershipGallery3 from "./TrainingProgramImages/cayep4.jpg";

import managementGallery1 from "./TrainingProgramImages/entrepreneurship.jpg";
import managementGallery2 from "./TrainingProgramImages/entrepreneurship.jpg";

import entrepreneurshipGallery1 from "./TrainingProgramImages/entrepreneurship.jpg";
import entrepreneurshipGallery2 from "./TrainingProgramImages/entrepreneurship.jpg";

import communicationGallery1 from "./TrainingProgramImages/cayep5.jpg";
import communicationGallery2 from "./TrainingProgramImages/entrepreneurship.jpg";

import Footer from "./Footer";

const trainingPrograms = [
    {
        id: 1,
        category: "Leadership Development",
        title: "Leadership Development Training",
        shortTitle: "Leadership",
        icon: <FaUsers />,
        image: leadershipImage,
        description:
            "A practical leadership development program designed to help learners understand leadership principles, strengthen personal effectiveness, communicate with confidence, and develop the mindset required to lead people and responsibilities with purpose.",
        duration: "Flexible Training",
        level: "Professional Development",
        focus: "Leadership & Personal Growth",
        objectives: [
            "Understand practical leadership principles",
            "Develop confidence and personal responsibility",
            "Strengthen communication and teamwork",
            "Improve decision-making and problem-solving",
            "Develop a purpose-driven leadership mindset",
        ],
        gallery: [
            leadershipImage,
            leadershipGallery1,
            leadershipGallery2,
            leadershipGallery3,
        ],
    },

    {
        id: 2,
        category: "Business Management",
        title: "Business Management Training",
        shortTitle: "Management",
        icon: <FaChartLine />,
        image: managementImage,
        description:
            "A structured learning program focused on practical business management skills, organizational thinking, planning, coordination, workplace responsibility, and the principles needed to manage people, tasks, and resources effectively.",
        duration: "Flexible Training",
        level: "Professional Development",
        focus: "Business & Management",
        objectives: [
            "Understand fundamental management principles",
            "Develop practical planning skills",
            "Improve organizational and coordination abilities",
            "Strengthen workplace decision-making",
            "Build effective management habits",
        ],
        gallery: [
            managementImage,
            managementGallery1,
            managementGallery2,
        ],
    },

    {
        id: 3,
        category: "Entrepreneurship",
        title: "Entrepreneurship & Business Development",
        shortTitle: "Entrepreneurship",
        icon: <FaLightbulb />,
        image: entrepreneurshipImage,
        description:
            "A practical entrepreneurship program that helps learners explore business ideas, identify opportunities, understand customers, think creatively, and develop the skills needed to move from an idea toward meaningful business action.",
        duration: "Flexible Training",
        level: "Career & Business Development",
        focus: "Entrepreneurship & Innovation",
        objectives: [
            "Develop an entrepreneurial mindset",
            "Identify and evaluate business opportunities",
            "Understand basic business planning",
            "Develop creative problem-solving skills",
            "Connect ideas with practical action",
        ],
        gallery: [
            entrepreneurshipImage,
            entrepreneurshipGallery1,
            entrepreneurshipGallery2,
        ],
    },

    {
        id: 4,
        category: "Communication & Professional Skills",
        title: "Communication & Professional Development",
        shortTitle: "Communication",
        icon: <FaBullseye />,
        image: communicationImage,
        description:
            "A professional skills program focused on communication, presentation, confidence, workplace interaction, professional conduct, and the practical abilities learners need to express ideas clearly and work effectively with others.",
        duration: "Flexible Training",
        level: "Professional Development",
        focus: "Communication & Career Growth",
        objectives: [
            "Improve professional communication",
            "Build presentation confidence",
            "Develop effective listening skills",
            "Strengthen workplace interaction",
            "Improve professional self-expression",
        ],
        gallery: [
            communicationImage,
            communicationGallery1,
            communicationGallery2,
        ],
    },
];

const LearningIcon = ({ type }) => {
    const icons = {
        purpose: <FaBullseye />,
        growth: <FaSeedling />,
        leadership: <FaUsers />,
        impact: <FaChartLine />,
    };

    return icons[type] || <FaBookOpen />;
};

const CampusPage = () => {
    const [activeProgram, setActiveProgram] = useState(0);
    const [activeImage, setActiveImage] = useState(0);

    const program = trainingPrograms[activeProgram];

    useEffect(() => {
        setActiveImage(0);
    }, [activeProgram]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "ArrowRight") {
                setActiveProgram(
                    (current) => (current + 1) % trainingPrograms.length
                );
            }

            if (event.key === "ArrowLeft") {
                setActiveProgram(
                    (current) =>
                        (current - 1 + trainingPrograms.length) %
                        trainingPrograms.length
                );
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const nextProgram = () => {
        setActiveProgram(
            (current) => (current + 1) % trainingPrograms.length
        );
    };

    const previousProgram = () => {
        setActiveProgram(
            (current) =>
                (current - 1 + trainingPrograms.length) %
                trainingPrograms.length
        );
    };

    return (
        <div className="cayep-training-page">
            <style>{`
                .cayep-training-page {
                    --navy: #062A4B;
                    --deep-blue: #063A6B;
                    --black: #0A0F12;
                    --gold: #F6C944;
                    --yellow: #EFA81C;
                    --orange: #C0750D;
                    --green: #4FAE22;
                    --olive: #5F6622;
                    --sky: #0F6DAB;
                    --white: #F0EDEA;
                    --gray: #C4C7C7;
                    --antique: #CBA454;

                    min-height: 100vh;
                    width: 100%;
                    overflow: hidden;
                    background: #f7f9fa;
                    color: var(--black);
                    font-family: "Poppins", "Inter", sans-serif;
                }

                .cayep-training-page * {
                    box-sizing: border-box;
                }

                .cayep-training-page button,
                .cayep-training-page a {
                    font-family: inherit;
                }

                /* =====================================================
                   HERO
                ===================================================== */

                .cayep-training-hero {
                    position: relative;
                    min-height: 600px;
                    overflow: hidden;
                    background:
                        radial-gradient(
                            circle at 82% 20%,
                            rgba(246, 201, 68, 0.18),
                            transparent 27%
                        ),
                        radial-gradient(
                            circle at 10% 90%,
                            rgba(15, 109, 171, 0.2),
                            transparent 30%
                        ),
                        var(--navy);
                }

                .cayep-training-hero::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    opacity: 0.12;
                    background-image:
                        linear-gradient(
                            rgba(255,255,255,0.4) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            90deg,
                            rgba(255,255,255,0.4) 1px,
                            transparent 1px
                        );
                    background-size: 70px 70px;
                    animation: cayepGridMove 20s linear infinite;
                }

                @keyframes cayepGridMove {
                    from {
                        transform: translate(0, 0);
                    }

                    to {
                        transform: translate(70px, 70px);
                    }
                }

                .cayep-floating-shape {
                    position: absolute;
                    border-radius: 50%;
                    pointer-events: none;
                    filter: blur(2px);
                }

                .cayep-floating-shape.one {
                    width: 420px;
                    height: 420px;
                    right: -180px;
                    top: -190px;
                    background:
                        radial-gradient(
                            circle,
                            rgba(246,201,68,0.24),
                            transparent 70%
                        );
                    animation: cayepFloatOne 8s ease-in-out infinite;
                }

                .cayep-floating-shape.two {
                    width: 350px;
                    height: 350px;
                    left: -180px;
                    bottom: -200px;
                    background:
                        radial-gradient(
                            circle,
                            rgba(79,174,34,0.2),
                            transparent 70%
                        );
                    animation: cayepFloatTwo 9s ease-in-out infinite;
                }

                @keyframes cayepFloatOne {
                    0%, 100% {
                        transform: translate(0, 0) scale(1);
                    }

                    50% {
                        transform: translate(-35px, 35px) scale(1.08);
                    }
                }

                @keyframes cayepFloatTwo {
                    0%, 100% {
                        transform: translate(0, 0);
                    }

                    50% {
                        transform: translate(40px, -35px);
                    }
                }

                .cayep-hero-content {
                    position: relative;
                    z-index: 3;
                    width: min(1250px, 90%);
                    min-height: 600px;
                    margin: auto;
                    display: grid;
                    grid-template-columns: 1.05fr 0.95fr;
                    align-items: center;
                    gap: 70px;
                    padding: 80px 0;
                }

                .cayep-hero-copy {
                    max-width: 720px;
                }

                .cayep-hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    padding: 9px 15px;
                    margin-bottom: 22px;
                    border: 1px solid rgba(246,201,68,0.4);
                    border-radius: 30px;
                    background: rgba(246,201,68,0.09);
                    color: #ffe58a;
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 1.7px;
                    text-transform: uppercase;
                    backdrop-filter: blur(10px);
                }

                .cayep-hero-badge svg {
                    color: var(--gold);
                }

                .cayep-hero-copy h1 {
                    margin: 0;
                    color: #ffffff;
                    font-size: clamp(42px, 6vw, 76px);
                    line-height: 1.02;
                    font-weight: 900;
                    letter-spacing: -3px;
                }

                .cayep-hero-copy h1 span {
                    color: var(--gold);
                }

                .cayep-hero-copy p {
                    max-width: 700px;
                    margin: 25px 0 0;
                    color: #d9e2e8;
                    font-size: 15px;
                    line-height: 1.9;
                }

                .cayep-hero-actions {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-top: 30px;
                }

                .cayep-hero-action {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    padding: 13px 18px;
                    border-radius: 10px;
                    text-decoration: none;
                    font-size: 11px;
                    font-weight: 800;
                    transition: 0.25s ease;
                }

                .cayep-hero-action.primary {
                    background: var(--gold);
                    color: var(--navy);
                }

                .cayep-hero-action.primary:hover {
                    background: #ffd965;
                    transform: translateY(-3px);
                }

                .cayep-hero-action.secondary {
                    border: 1px solid rgba(255,255,255,0.2);
                    color: #ffffff;
                    background: rgba(255,255,255,0.06);
                }

                .cayep-hero-action.secondary:hover {
                    background: rgba(255,255,255,0.12);
                    transform: translateY(-3px);
                }

                /* =====================================================
                   HERO PROGRAM VISUAL
                ===================================================== */

                .cayep-program-visual {
                    position: relative;
                    min-height: 450px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .cayep-program-orbit {
                    position: absolute;
                    width: 390px;
                    height: 390px;
                    border: 1px solid rgba(246,201,68,0.2);
                    border-radius: 50%;
                    animation: cayepOrbit 18s linear infinite;
                }

                .cayep-program-orbit::before,
                .cayep-program-orbit::after {
                    content: "";
                    position: absolute;
                    border-radius: 50%;
                }

                .cayep-program-orbit::before {
                    width: 12px;
                    height: 12px;
                    top: 25px;
                    left: 50%;
                    background: var(--gold);
                    box-shadow: 0 0 30px rgba(246,201,68,0.8);
                }

                .cayep-program-orbit::after {
                    width: 8px;
                    height: 8px;
                    bottom: 45px;
                    right: 45px;
                    background: var(--green);
                    box-shadow: 0 0 25px rgba(79,174,34,0.8);
                }

                @keyframes cayepOrbit {
                    from {
                        transform: rotate(0deg);
                    }

                    to {
                        transform: rotate(360deg);
                    }
                }

                .cayep-learning-stack {
                    position: relative;
                    z-index: 2;
                    width: min(430px, 100%);
                    padding: 18px;
                }

                .cayep-learning-card {
                    position: relative;
                    overflow: hidden;
                    padding: 28px;
                    border: 1px solid rgba(255,255,255,0.15);
                    border-radius: 24px;
                    background:
                        linear-gradient(
                            145deg,
                            rgba(255,255,255,0.13),
                            rgba(255,255,255,0.045)
                        );
                    box-shadow:
                        0 30px 80px rgba(0,0,0,0.28);
                    backdrop-filter: blur(18px);
                }

                .cayep-learning-card::before {
                    content: "";
                    position: absolute;
                    width: 180px;
                    height: 180px;
                    right: -70px;
                    top: -70px;
                    border-radius: 50%;
                    background: rgba(246,201,68,0.16);
                }

                .cayep-learning-card-top {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 35px;
                }

                .cayep-learning-card-icon {
                    width: 54px;
                    height: 54px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 16px;
                    background: var(--gold);
                    color: var(--navy);
                    font-size: 22px;
                }

                .cayep-learning-card-number {
                    color: rgba(255,255,255,0.5);
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.5px;
                }

                .cayep-learning-card h3 {
                    position: relative;
                    z-index: 2;
                    margin: 0;
                    color: #ffffff;
                    font-size: 27px;
                    line-height: 1.2;
                    font-weight: 850;
                }

                .cayep-learning-card p {
                    position: relative;
                    z-index: 2;
                    margin: 12px 0 0;
                    color: #c9d4db;
                    font-size: 12px;
                    line-height: 1.8;
                }

                .cayep-learning-mini-stats {
                    position: relative;
                    z-index: 2;
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px;
                    margin-top: 28px;
                }

                .cayep-mini-stat {
                    padding: 13px;
                    border-radius: 12px;
                    background: rgba(255,255,255,0.06);
                    border: 1px solid rgba(255,255,255,0.08);
                }

                .cayep-mini-stat strong {
                    display: block;
                    color: var(--gold);
                    font-size: 12px;
                    font-weight: 800;
                }

                .cayep-mini-stat span {
                    display: block;
                    margin-top: 4px;
                    color: #b9c7d0;
                    font-size: 9px;
                }

                .cayep-hero-bottom {
                    position: absolute;
                    z-index: 5;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    display: flex;
                    height: 6px;
                }

                .cayep-hero-bottom span:nth-child(1) {
                    flex: 2;
                    background: var(--gold);
                }

                .cayep-hero-bottom span:nth-child(2) {
                    flex: 1;
                    background: var(--green);
                }

                .cayep-hero-bottom span:nth-child(3) {
                    flex: 1;
                    background: var(--sky);
                }

                /* =====================================================
                   INTRO
                ===================================================== */

                .cayep-program-intro {
                    padding: 100px 5% 60px;
                    background: #ffffff;
                    text-align: center;
                }

                .cayep-section-container {
                    width: min(1150px, 100%);
                    margin: auto;
                }

                .cayep-section-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: var(--orange);
                    font-size: 10px;
                    font-weight: 850;
                    letter-spacing: 1.7px;
                    text-transform: uppercase;
                }

                .cayep-section-label svg {
                    color: var(--green);
                }

                .cayep-program-intro h2 {
                    margin: 14px 0 15px;
                    color: var(--navy);
                    font-size: clamp(31px, 4vw, 49px);
                    line-height: 1.12;
                    font-weight: 900;
                    letter-spacing: -1.8px;
                }

                .cayep-program-intro h2 span {
                    color: var(--orange);
                }

                .cayep-program-intro p {
                    max-width: 760px;
                    margin: auto;
                    color: #61717d;
                    font-size: 14px;
                    line-height: 1.9;
                }

                /* =====================================================
                   PROGRAM VIEWER
                ===================================================== */

                .cayep-program-viewer {
                    position: relative;
                    padding: 30px 5% 110px;
                    background:
                        linear-gradient(
                            180deg,
                            #ffffff 0%,
                            #f4f7f8 100%
                        );
                }

                .cayep-program-viewer-container {
                    width: min(1250px, 100%);
                    margin: auto;
                }

                .cayep-program-navigation {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    margin-bottom: 25px;
                }

                .cayep-program-counter {
                    color: var(--navy);
                    font-size: 11px;
                    font-weight: 850;
                    letter-spacing: 1.5px;
                }

                .cayep-program-counter span {
                    color: var(--orange);
                }

                .cayep-program-arrows {
                    display: flex;
                    gap: 8px;
                }

                .cayep-program-arrow {
                    width: 45px;
                    height: 45px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #dce3e7;
                    border-radius: 12px;
                    background: #ffffff;
                    color: var(--navy);
                    cursor: pointer;
                    transition: 0.25s ease;
                    box-shadow: 0 8px 25px rgba(6,42,75,0.05);
                }

                .cayep-program-arrow:hover {
                    border-color: var(--gold);
                    background: var(--gold);
                    transform: translateY(-3px);
                }

                .cayep-program-progress {
                    height: 4px;
                    margin-bottom: 25px;
                    overflow: hidden;
                    border-radius: 20px;
                    background: #dfe5e8;
                }

                .cayep-program-progress span {
                    display: block;
                    height: 100%;
                    border-radius: inherit;
                    background:
                        linear-gradient(
                            90deg,
                            var(--gold),
                            var(--orange),
                            var(--green)
                        );
                    transition: width 0.5s ease;
                }

                .cayep-program-main {
                    display: grid;
                    grid-template-columns: 0.95fr 1.05fr;
                    min-height: 580px;
                    overflow: hidden;
                    border: 1px solid #dde4e7;
                    border-radius: 30px;
                    background: #ffffff;
                    box-shadow:
                        0 25px 70px rgba(6,42,75,0.09);
                }

                /* =====================================================
                   PROGRAM IMAGE
                ===================================================== */

                .cayep-program-gallery {
                    position: relative;
                    min-height: 580px;
                    overflow: hidden;
                    background: var(--navy);
                }

                .cayep-program-gallery-main {
                    position: absolute;
                    inset: 0;
                }

                .cayep-program-gallery-main img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .cayep-gallery-overlay {
                    position: absolute;
                    inset: 0;
                    background:
                        linear-gradient(
                            180deg,
                            rgba(6,42,75,0.08) 25%,
                            rgba(6,42,75,0.9) 100%
                        );
                }

                .cayep-gallery-category {
                    position: absolute;
                    z-index: 4;
                    top: 25px;
                    left: 25px;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 9px 13px;
                    border-radius: 30px;
                    background: rgba(6,42,75,0.82);
                    color: #ffffff;
                    font-size: 9px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    text-transform: uppercase;
                    backdrop-filter: blur(10px);
                }

                .cayep-gallery-category svg {
                    color: var(--gold);
                }

                .cayep-gallery-number {
                    position: absolute;
                    z-index: 4;
                    top: 28px;
                    right: 28px;
                    color: rgba(255,255,255,0.8);
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 1px;
                }

                .cayep-gallery-caption {
                    position: absolute;
                    z-index: 4;
                    left: 30px;
                    right: 30px;
                    bottom: 105px;
                }

                .cayep-gallery-caption small {
                    color: var(--gold);
                    font-size: 9px;
                    font-weight: 850;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                }

                .cayep-gallery-caption h3 {
                    margin: 7px 0 0;
                    color: #ffffff;
                    font-size: 27px;
                    line-height: 1.2;
                    font-weight: 850;
                }

                .cayep-gallery-thumbnails {
                    position: absolute;
                    z-index: 6;
                    left: 25px;
                    right: 25px;
                    bottom: 25px;
                    display: flex;
                    gap: 8px;
                    overflow-x: auto;
                    padding-bottom: 2px;
                }

                .cayep-gallery-thumbnail {
                    flex: 0 0 62px;
                    width: 62px;
                    height: 50px;
                    padding: 0;
                    overflow: hidden;
                    border: 2px solid transparent;
                    border-radius: 8px;
                    background: transparent;
                    cursor: pointer;
                    opacity: 0.62;
                    transition: 0.25s ease;
                }

                .cayep-gallery-thumbnail img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .cayep-gallery-thumbnail:hover,
                .cayep-gallery-thumbnail.active {
                    border-color: var(--gold);
                    opacity: 1;
                    transform: translateY(-3px);
                }

                /* =====================================================
                   PROGRAM DETAILS
                ===================================================== */

                .cayep-program-details {
                    position: relative;
                    padding: 50px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .cayep-detail-top {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    margin-bottom: 18px;
                }

                .cayep-detail-icon {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 14px;
                    background: #fff7d7;
                    color: var(--orange);
                    font-size: 18px;
                }

                .cayep-detail-top span {
                    color: var(--orange);
                    font-size: 10px;
                    font-weight: 850;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                }

                .cayep-program-details h2 {
                    margin: 0;
                    max-width: 650px;
                    color: var(--navy);
                    font-size: clamp(32px, 4vw, 52px);
                    line-height: 1.05;
                    font-weight: 900;
                    letter-spacing: -2px;
                }

                .cayep-program-details > p {
                    margin: 22px 0 27px;
                    color: #62727d;
                    font-size: 13px;
                    line-height: 1.9;
                }

                .cayep-program-meta {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 9px;
                    margin-bottom: 28px;
                }

                .cayep-meta-item {
                    padding: 13px;
                    border: 1px solid #e1e7ea;
                    border-radius: 12px;
                    background: #f8fafb;
                }

                .cayep-meta-item svg {
                    margin-bottom: 7px;
                    color: var(--green);
                    font-size: 12px;
                }

                .cayep-meta-item strong {
                    display: block;
                    color: var(--navy);
                    font-size: 9px;
                    font-weight: 850;
                }

                .cayep-meta-item span {
                    display: block;
                    margin-top: 4px;
                    color: #7b8992;
                    font-size: 8px;
                    line-height: 1.4;
                }

                .cayep-objectives-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 13px;
                    color: var(--navy);
                    font-size: 11px;
                    font-weight: 850;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                }

                .cayep-objectives-title svg {
                    color: var(--green);
                }

                .cayep-objectives {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 9px;
                    margin: 0;
                    padding: 0;
                    list-style: none;
                }

                .cayep-objective {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                    color: #596973;
                    font-size: 10px;
                    line-height: 1.6;
                }

                .cayep-objective svg {
                    flex: 0 0 auto;
                    margin-top: 3px;
                    color: var(--green);
                    font-size: 9px;
                }

                .cayep-detail-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    margin-top: 30px;
                    padding-top: 22px;
                    border-top: 1px solid #e5eaed;
                }

                .cayep-detail-footer-note {
                    color: #7b8992;
                    font-size: 9px;
                    line-height: 1.6;
                }

                .cayep-explore-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    padding: 13px 17px;
                    border-radius: 10px;
                    background: var(--navy);
                    color: #ffffff;
                    text-decoration: none;
                    font-size: 10px;
                    font-weight: 800;
                    white-space: nowrap;
                    transition: 0.25s ease;
                }

                .cayep-explore-button:hover {
                    background: var(--deep-blue);
                    transform: translateY(-3px);
                }

                /* =====================================================
                   PROGRAM SELECTOR
                ===================================================== */

                .cayep-program-selector {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 10px;
                    margin-top: 18px;
                }

                .cayep-selector-button {
                    position: relative;
                    overflow: hidden;
                    min-height: 80px;
                    padding: 15px;
                    border: 1px solid #dfe6e9;
                    border-radius: 13px;
                    background: #ffffff;
                    color: var(--navy);
                    text-align: left;
                    cursor: pointer;
                    transition: 0.3s ease;
                }

                .cayep-selector-button::after {
                    content: "";
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    height: 3px;
                    background: transparent;
                    transition: 0.3s ease;
                }

                .cayep-selector-button:hover {
                    transform: translateY(-3px);
                    border-color: var(--antique);
                }

                .cayep-selector-button.active {
                    border-color: var(--gold);
                    box-shadow: 0 10px 25px rgba(6,42,75,0.08);
                }

                .cayep-selector-button.active::after {
                    background: var(--gold);
                }

                .cayep-selector-icon {
                    color: var(--orange);
                    font-size: 15px;
                }

                .cayep-selector-button strong {
                    display: block;
                    margin-top: 8px;
                    font-size: 10px;
                    line-height: 1.3;
                }

                .cayep-selector-button span {
                    display: block;
                    margin-top: 4px;
                    color: #8a979f;
                    font-size: 8px;
                }

                /* =====================================================
                   LEARNING JOURNEY
                ===================================================== */

                .cayep-journey {
                    padding: 100px 5%;
                    background: var(--navy);
                    overflow: hidden;
                }

                .cayep-journey-container {
                    width: min(1150px, 100%);
                    margin: auto;
                }

                .cayep-journey-header {
                    max-width: 700px;
                    margin-bottom: 50px;
                }

                .cayep-journey-header .cayep-section-label {
                    color: var(--gold);
                }

                .cayep-journey-header h2 {
                    margin: 13px 0;
                    color: #ffffff;
                    font-size: clamp(30px, 4vw, 45px);
                    line-height: 1.1;
                    font-weight: 900;
                    letter-spacing: -1.5px;
                }

                .cayep-journey-header p {
                    margin: 0;
                    color: #b9c8d1;
                    font-size: 13px;
                    line-height: 1.8;
                }

                .cayep-journey-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                }

                .cayep-journey-card {
                    position: relative;
                    padding: 27px 22px;
                    border: 1px solid rgba(255,255,255,0.11);
                    border-radius: 19px;
                    background: rgba(255,255,255,0.045);
                    transition: 0.3s ease;
                }

                .cayep-journey-card:hover {
                    transform: translateY(-8px);
                    border-color: rgba(246,201,68,0.35);
                    background: rgba(255,255,255,0.075);
                }

                .cayep-journey-number {
                    color: rgba(255,255,255,0.25);
                    font-size: 9px;
                    font-weight: 850;
                    letter-spacing: 1px;
                }

                .cayep-journey-icon {
                    width: 46px;
                    height: 46px;
                    margin: 18px 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 13px;
                    background: rgba(246,201,68,0.12);
                    color: var(--gold);
                }

                .cayep-journey-card h3 {
                    margin: 0 0 8px;
                    color: #ffffff;
                    font-size: 16px;
                    font-weight: 800;
                }

                .cayep-journey-card p {
                    margin: 0;
                    color: #aebdc6;
                    font-size: 10px;
                    line-height: 1.7;
                }

                /* =====================================================
                   CTA
                ===================================================== */

                .cayep-training-cta {
                    padding: 90px 5%;
                    background: #ffffff;
                }

                .cayep-training-cta-box {
                    position: relative;
                    width: min(1200px, 100%);
                    margin: auto;
                    overflow: hidden;
                    padding: 55px;
                    border-radius: 27px;
                    background:
                        linear-gradient(
                            125deg,
                            var(--deep-blue),
                            var(--navy)
                        );
                    box-shadow:
                        0 25px 70px rgba(6,42,75,0.14);
                }

                .cayep-training-cta-box::before {
                    content: "";
                    position: absolute;
                    width: 360px;
                    height: 360px;
                    right: -130px;
                    top: -190px;
                    border-radius: 50%;
                    background: rgba(246,201,68,0.16);
                }

                .cayep-training-cta-box::after {
                    content: "";
                    position: absolute;
                    width: 200px;
                    height: 200px;
                    left: -100px;
                    bottom: -120px;
                    border-radius: 50%;
                    background: rgba(79,174,34,0.13);
                }

                .cayep-cta-content {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 30px;
                }

                .cayep-cta-content h2 {
                    margin: 0 0 10px;
                    color: #ffffff;
                    font-size: clamp(26px, 4vw, 42px);
                    line-height: 1.1;
                    font-weight: 900;
                    letter-spacing: -1.3px;
                }

                .cayep-cta-content p {
                    max-width: 680px;
                    margin: 0;
                    color: #c8d4dc;
                    font-size: 13px;
                    line-height: 1.8;
                }

                .cayep-cta-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    padding: 15px 21px;
                    border-radius: 11px;
                    background: var(--gold);
                    color: var(--navy);
                    text-decoration: none;
                    font-size: 11px;
                    font-weight: 850;
                    white-space: nowrap;
                    transition: 0.25s ease;
                }

                .cayep-cta-button:hover {
                    transform: translateY(-3px);
                    background: #ffda62;
                }

                /* =====================================================
                   RESPONSIVE
                ===================================================== */

                @media (max-width: 1050px) {
                    .cayep-hero-content {
                        grid-template-columns: 1fr;
                        gap: 25px;
                    }

                    .cayep-hero-copy {
                        max-width: 850px;
                    }

                    .cayep-program-visual {
                        min-height: 380px;
                    }

                    .cayep-program-main {
                        grid-template-columns: 1fr;
                    }

                    .cayep-program-gallery {
                        min-height: 500px;
                    }

                    .cayep-program-selector {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .cayep-journey-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 760px) {
                    .cayep-training-hero {
                        min-height: auto;
                    }

                    .cayep-hero-content {
                        min-height: auto;
                        padding: 90px 0 75px;
                    }

                    .cayep-hero-copy h1 {
                        font-size: 48px;
                        letter-spacing: -2px;
                    }

                    .cayep-program-visual {
                        min-height: 340px;
                    }

                    .cayep-program-orbit {
                        width: 300px;
                        height: 300px;
                    }

                    .cayep-learning-card {
                        padding: 24px;
                    }

                    .cayep-program-intro {
                        padding: 75px 6% 45px;
                    }

                    .cayep-program-viewer {
                        padding: 20px 6% 75px;
                    }

                    .cayep-program-details {
                        padding: 35px 27px;
                    }

                    .cayep-program-meta {
                        grid-template-columns: 1fr;
                    }

                    .cayep-objectives {
                        grid-template-columns: 1fr;
                    }

                    .cayep-detail-footer {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .cayep-explore-button {
                        width: 100%;
                        justify-content: center;
                    }

                    .cayep-journey {
                        padding: 75px 6%;
                    }

                    .cayep-training-cta {
                        padding: 70px 5%;
                    }

                    .cayep-training-cta-box {
                        padding: 35px 25px;
                    }

                    .cayep-cta-content {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .cayep-cta-button {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 520px) {
                    .cayep-hero-copy h1 {
                        font-size: 39px;
                    }

                    .cayep-hero-copy p {
                        font-size: 12px;
                    }

                    .cayep-hero-actions {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .cayep-hero-action {
                        justify-content: center;
                    }

                    .cayep-program-navigation {
                        align-items: flex-start;
                    }

                    .cayep-program-gallery {
                        min-height: 400px;
                    }

                    .cayep-gallery-caption {
                        left: 20px;
                        right: 20px;
                        bottom: 95px;
                    }

                    .cayep-gallery-caption h3 {
                        font-size: 22px;
                    }

                    .cayep-gallery-thumbnails {
                        left: 20px;
                        right: 20px;
                        bottom: 20px;
                    }

                    .cayep-program-details h2 {
                        font-size: 34px;
                    }

                    .cayep-program-selector {
                        grid-template-columns: 1fr;
                    }

                    .cayep-journey-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-mini-stat {
                        padding: 10px;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .cayep-training-page *,
                    .cayep-training-page *::before,
                    .cayep-training-page *::after {
                        animation: none !important;
                        transition: none !important;
                    }
                }
            `}</style>

            {/* =====================================================
                HERO
            ===================================================== */}

            <section className="cayep-training-hero">
                <div className="cayep-floating-shape one"></div>
                <div className="cayep-floating-shape two"></div>

                <div className="cayep-hero-content">
                    <motion.div
                        className="cayep-hero-copy"
                        initial={{ opacity: 0, x: -55 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                            duration: 0.9,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                    >
                        <motion.div
                            className="cayep-hero-badge"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            <FaGraduationCap />
                            CAYEP LEADERSHIP TRAINING
                        </motion.div>

                        <h1>
                            Educate.
                            <br />
                            <span>Equip. Empower.</span>
                        </h1>

                        <p>
                            Explore CAYEP training programs designed to
                            develop practical knowledge, leadership ability,
                            professional confidence, and skills that can be
                            applied beyond the classroom.
                        </p>

                        <div className="cayep-hero-actions">
                            <a
                                href="#training-programs"
                                className="cayep-hero-action primary"
                            >
                                Explore Programs
                                <FaArrowRight />
                            </a>

                            <a
                                href="#learning-journey"
                                className="cayep-hero-action secondary"
                            >
                                Our Learning Journey
                                <FaCompass />
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        className="cayep-program-visual"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                            duration: 1,
                            delay: 0.15,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                    >
                        <div className="cayep-program-orbit"></div>

                        <motion.div
                            className="cayep-learning-stack"
                            animate={{
                                y: [0, -10, 0],
                            }}
                            transition={{
                                duration: 5,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <div className="cayep-learning-card">
                                <div className="cayep-learning-card-top">
                                    <div className="cayep-learning-card-icon">
                                        <FaGraduationCap />
                                    </div>

                                    <span className="cayep-learning-card-number">
                                        CAYEP / 01
                                    </span>
                                </div>

                                <h3>
                                    Learning That Develops
                                    <br />
                                    People.
                                </h3>

                                <p>
                                    Build knowledge, strengthen capability,
                                    develop confidence, and prepare to lead
                                    with purpose.
                                </p>

                                <div className="cayep-learning-mini-stats">
                                    <div className="cayep-mini-stat">
                                        <strong>LEARN</strong>
                                        <span>Build knowledge</span>
                                    </div>

                                    <div className="cayep-mini-stat">
                                        <strong>GROW</strong>
                                        <span>Develop capability</span>
                                    </div>

                                    <div className="cayep-mini-stat">
                                        <strong>LEAD</strong>
                                        <span>Apply your skills</span>
                                    </div>

                                    <div className="cayep-mini-stat">
                                        <strong>IMPACT</strong>
                                        <span>Create meaningful value</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                <div className="cayep-hero-bottom">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </section>

            {/* =====================================================
                INTRO
            ===================================================== */}

            <section className="cayep-program-intro">
                <div className="cayep-section-container">
                    <motion.div
                        className="cayep-section-label"
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <FaBookOpen />
                        OUR TRAINING PROGRAMS
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 25 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.65 }}
                    >
                        Training Designed For
                        <span> Growth.</span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.65, delay: 0.1 }}
                    >
                        Explore our learning areas and move between programs
                        using the navigation arrows. Select a program to view
                        its description, learning objectives, training focus,
                        and available images.
                    </motion.p>
                </div>
            </section>

            {/* =====================================================
                PROGRAM VIEWER
            ===================================================== */}

            <section
                id="training-programs"
                className="cayep-program-viewer"
            >
                <div className="cayep-program-viewer-container">
                    <div className="cayep-program-navigation">
                        <div className="cayep-program-counter">
                            PROGRAM{" "}
                            <span>
                                {String(activeProgram + 1).padStart(2, "0")}
                            </span>
                            {" / "}
                            {String(trainingPrograms.length).padStart(
                                2,
                                "0"
                            )}
                        </div>

                        <div className="cayep-program-arrows">
                            <button
                                type="button"
                                className="cayep-program-arrow"
                                onClick={previousProgram}
                                aria-label="Previous training program"
                                title="Previous training program"
                            >
                                <FaArrowLeft />
                            </button>

                            <button
                                type="button"
                                className="cayep-program-arrow"
                                onClick={nextProgram}
                                aria-label="Next training program"
                                title="Next training program"
                            >
                                <FaArrowRight />
                            </button>
                        </div>
                    </div>

                    <div className="cayep-program-progress">
                        <motion.span
                            animate={{
                                width: `${
                                    ((activeProgram + 1) /
                                        trainingPrograms.length) *
                                    100
                                }%`,
                            }}
                            transition={{
                                duration: 0.5,
                                ease: "easeOut",
                            }}
                        />
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={program.id}
                            className="cayep-program-main"
                            initial={{
                                opacity: 0,
                                x: 45,
                                scale: 0.985,
                            }}
                            animate={{
                                opacity: 1,
                                x: 0,
                                scale: 1,
                            }}
                            exit={{
                                opacity: 0,
                                x: -45,
                                scale: 0.985,
                            }}
                            transition={{
                                duration: 0.5,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                        >
                            {/* IMAGE / GALLERY */}

                            <div className="cayep-program-gallery">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeImage}
                                        className="cayep-program-gallery-main"
                                        initial={{
                                            opacity: 0,
                                            scale: 1.06,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            scale: 1,
                                        }}
                                        exit={{
                                            opacity: 0,
                                            scale: 0.98,
                                        }}
                                        transition={{
                                            duration: 0.55,
                                        }}
                                    >
                                        <img
                                            src={
                                                program.gallery[activeImage]
                                            }
                                            alt={`${program.title} training`}
                                        />

                                        <div className="cayep-gallery-overlay"></div>
                                    </motion.div>
                                </AnimatePresence>

                                <div className="cayep-gallery-category">
                                    {program.icon}
                                    {program.category}
                                </div>

                                <div className="cayep-gallery-number">
                                    {String(program.id).padStart(2, "0")}
                                </div>

                                <div className="cayep-gallery-caption">
                                    <small>CAYEP TRAINING PROGRAM</small>

                                    <h3>{program.title}</h3>
                                </div>

                                <div className="cayep-gallery-thumbnails">
                                    {program.gallery.map(
                                        (galleryImage, index) => (
                                            <button
                                                type="button"
                                                key={`${program.id}-${index}`}
                                                className={`cayep-gallery-thumbnail ${
                                                    activeImage === index
                                                        ? "active"
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    setActiveImage(index)
                                                }
                                                aria-label={`View image ${
                                                    index + 1
                                                }`}
                                            >
                                                <img
                                                    src={galleryImage}
                                                    alt={`${program.title} ${
                                                        index + 1
                                                    }`}
                                                />
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>

                            {/* PROGRAM DETAILS */}

                            <div className="cayep-program-details">
                                <div className="cayep-detail-top">
                                    <div className="cayep-detail-icon">
                                        {program.icon}
                                    </div>

                                    <span>{program.category}</span>
                                </div>

                                <h2>{program.title}</h2>

                                <p>{program.description}</p>

                                <div className="cayep-program-meta">
                                    <div className="cayep-meta-item">
                                        <FaClock />
                                        <strong>Training Format</strong>
                                        <span>{program.duration}</span>
                                    </div>

                                    <div className="cayep-meta-item">
                                        <FaGraduationCap />
                                        <strong>Development Level</strong>
                                        <span>{program.level}</span>
                                    </div>

                                    <div className="cayep-meta-item">
                                        <FaCompass />
                                        <strong>Primary Focus</strong>
                                        <span>{program.focus}</span>
                                    </div>
                                </div>

                                <div className="cayep-objectives-title">
                                    <FaBullseye />
                                    Learning Focus
                                </div>

                                <ul className="cayep-objectives">
                                    {program.objectives.map(
                                        (objective, index) => (
                                            <motion.li
                                                key={objective}
                                                className="cayep-objective"
                                                initial={{
                                                    opacity: 0,
                                                    x: 15,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    x: 0,
                                                }}
                                                transition={{
                                                    delay:
                                                        0.15 +
                                                        index * 0.07,
                                                }}
                                            >
                                                <FaCheckCircle />
                                                <span>{objective}</span>
                                            </motion.li>
                                        )
                                    )}
                                </ul>

                                <div className="cayep-detail-footer">
                                    <div className="cayep-detail-footer-note">
                                        Use the arrows above to explore the
                                        next or previous CAYEP training
                                        program.
                                    </div>

                                    <a
                                        href="#contact"
                                        className="cayep-explore-button"
                                    >
                                        Enquire About This Program
                                        <FaArrowRight />
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* PROGRAM SELECTOR */}

                    <div className="cayep-program-selector">
                        {trainingPrograms.map((item, index) => (
                            <button
                                type="button"
                                key={item.id}
                                className={`cayep-selector-button ${
                                    activeProgram === index
                                        ? "active"
                                        : ""
                                }`}
                                onClick={() => setActiveProgram(index)}
                            >
                                <div className="cayep-selector-icon">
                                    {item.icon}
                                </div>

                                <strong>{item.shortTitle}</strong>

                                <span>{item.category}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* =====================================================
                LEARNING JOURNEY
            ===================================================== */}

            <section
                id="learning-journey"
                className="cayep-journey"
            >
                <div className="cayep-journey-container">
                    <motion.div
                        className="cayep-journey-header"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <div className="cayep-section-label">
                            <FaGraduationCap />
                            THE CAYEP APPROACH
                        </div>

                        <h2>
                            Learn With Purpose.
                            <br />
                            Grow With Direction.
                        </h2>

                        <p>
                            CAYEP connects learning with personal development,
                            professional growth, leadership, and practical
                            application.
                        </p>
                    </motion.div>

                    <div className="cayep-journey-grid">
                        {[
                            {
                                number: "01",
                                icon: "purpose",
                                title: "Learn",
                                text:
                                    "Build knowledge and understand the principles behind effective leadership and professional growth.",
                            },
                            {
                                number: "02",
                                icon: "growth",
                                title: "Grow",
                                text:
                                    "Develop practical skills, confidence, discipline, communication, and personal capability.",
                            },
                            {
                                number: "03",
                                icon: "leadership",
                                title: "Lead",
                                text:
                                    "Apply what you learn through responsible decision-making, teamwork, and purposeful action.",
                            },
                            {
                                number: "04",
                                icon: "impact",
                                title: "Impact",
                                text:
                                    "Use your knowledge and abilities to contribute meaningful value in your work and community.",
                            },
                        ].map((item, index) => (
                            <motion.div
                                key={item.number}
                                className="cayep-journey-card"
                                initial={{
                                    opacity: 0,
                                    y: 35,
                                }}
                                whileInView={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                viewport={{
                                    once: true,
                                    amount: 0.2,
                                }}
                                transition={{
                                    duration: 0.55,
                                    delay: index * 0.1,
                                }}
                            >
                                <div className="cayep-journey-number">
                                    {item.number}
                                </div>

                                <div className="cayep-journey-icon">
                                    <LearningIcon type={item.icon} />
                                </div>

                                <h3>{item.title}</h3>

                                <p>{item.text}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* =====================================================
                CTA
            ===================================================== */}

            <section className="cayep-training-cta">
                <div className="cayep-training-cta-box">
                    <div className="cayep-cta-content">
                        <div>
                            <h2>
                                Your Next Level
                                <br />
                                Begins With Learning.
                            </h2>

                            <p>
                                Explore a CAYEP training program, discover the
                                learning areas that match your goals, and take
                                the next step in your personal and professional
                                development.
                            </p>
                        </div>

                        <a
                            href="#contact"
                            className="cayep-cta-button"
                        >
                            Start Your Learning Journey
                            <FaArrowRight />
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default CampusPage;

